import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface ConfigOrder {
  id: number;
  typeOS: string;
  departmentId: number | null;
  groupId: number | null;
  statusClosedServiceOrder?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;

  isThirdParty?: boolean | null;

  transaction?: boolean | null;
  canVehicle?: boolean | null;
  canAditionalVehicle?: boolean | null;
  canClient?: boolean | null;
  canStock?: boolean | null;
  canServices?: boolean | null;

  active?: boolean | null;
  canClose?: boolean | null;
  closingStatusId?: number | null;
  arrayTransactionServiceOrderId?: number[] | null;

  group?: { id: number; name: string; description?: string | null } | null;
  department?: { id: number; name: string; description?: string | null; isActive?: boolean | null } | null;
}

export interface ConfigOrderCreate {
  typeOS: string;
  departmentId: number;
  groupId: number;
  statusClosedServiceOrder?: string | null;
  transaction?: boolean;
  isThirdParty?: boolean;
  canVehicle?: boolean;
  canAditionalVehicle?: boolean;
  canClient?: boolean;
  canStock?: boolean;
  canServices?: boolean;
  active?: boolean;
  canClose?: boolean;
  closingStatusId?: number | null;
  arrayTransactionServiceOrderId?: number[];
}

export interface ConfigOrderUpdate {
  typeOS?: string;
  departmentId?: number;
  groupId?: number;
  statusClosedServiceOrder?: string | null;
  transaction?: boolean;
  isThirdParty?: boolean;
  canVehicle?: boolean;
  canAditionalVehicle?: boolean;
  canClient?: boolean;
  canStock?: boolean;
  canServices?: boolean;
  active?: boolean;
  canClose?: boolean;
  closingStatusId?: number | null;
  arrayTransactionServiceOrderId?: number[];
}

@Injectable({ providedIn: 'root' })
export class ConfigOrdersService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'configorders';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /**
   * GET /configorders
   */
  getAll(): Observable<ConfigOrder[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar configurações de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /configorders/{id}
   */
  getById(id: number): Observable<ConfigOrder> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar configuração de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /configorders?groupId={groupId}
   */
  getByGroup(groupId: number): Observable<ConfigOrder[]> {
    const params = new HttpParams().set('groupId', String(groupId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar configurações de OS por grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /configorders?typeOS={typeOS}
   */
  getByTypeOS(typeOS: string): Observable<ConfigOrder[]> {
    const params = new HttpParams().set('typeOS', typeOS);
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar configurações de OS por tipo');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /configorders?withTransaction=true
   */
  getWithTransaction(): Observable<ConfigOrder[]> {
    const params = new HttpParams().set('withTransaction', 'true');
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar configurações de OS com transação');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /configorders
   */
  create(payload: ConfigOrderCreate): Observable<ConfigOrder> {
    const headers = this.getHeaders();

    const apiPayload = this.mapToApiPayload(payload);

    return this.http.post<any>(this.buildUrl(this.resourcePath), apiPayload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Configuração de OS criada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar configuração de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * PUT /configorders/{id}
   */
  update(id: number, payload: ConfigOrderUpdate): Observable<ConfigOrder> {
    const headers = this.getHeaders();

    const apiPayload = this.mapToApiPayload(payload);

    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), apiPayload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Configuração de OS atualizada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar configuração de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /configorders/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Configuração de OS removida com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover configuração de OS');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.configOrders)) return response.configOrders;
    if (Array.isArray(response?.data?.configOrders)) return response.data.configOrders;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.configOrder ?? response;
  }

  private mapFromDto(dto: any): ConfigOrder {
    const rawClosingStatusId =
      dto?.ClosingStatusId ??
      dto?.closingStatusId ??
      dto?.CloseStatusId ??
      dto?.closeStatusId ??
      dto?.StatusCloseId ??
      dto?.statusCloseId ??
      dto?.ClosingStatusServiceOrderId ??
      dto?.closingStatusServiceOrderId;

    const closingStatusId = Number(rawClosingStatusId);

    const rawId = dto?.Id ?? dto?.id ?? 0;
    const rawDepartmentId = dto?.DepartmentId ?? dto?.departmentId ?? null;
    const rawGroupId = dto?.GroupId ?? dto?.groupId ?? null;

    const id = Number(rawId);
    const departmentId = rawDepartmentId == null ? null : Number(rawDepartmentId);
    const groupId = rawGroupId == null ? null : Number(rawGroupId);

    return {
      id: Number.isFinite(id) ? id : 0,
      typeOS: dto?.TypeOS ?? dto?.typeOS ?? '',
      departmentId: typeof departmentId === 'number' && Number.isFinite(departmentId) ? departmentId : null,
      groupId: typeof groupId === 'number' && Number.isFinite(groupId) ? groupId : null,
      statusClosedServiceOrder:
        dto?.StatusClosedServiceOrder ??
        dto?.statusClosedServiceOrder ??
        dto?.StatusClosed ??
        dto?.statusClosed ??
        null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
      transaction: dto?.Transaction ?? dto?.transaction ?? null,
      isThirdParty: this.normalizeBoolean(
        dto?.IsThirdParty ??
          dto?.CanThirdParty ??
          dto?.canThirdParty ??
          dto?.isThirdParty ??
          dto?.IsThirdPartyServiceOrder ??
          dto?.isThirdPartyServiceOrder ??
          dto?.ThirdParty ??
          dto?.thirdParty
      ),
      canVehicle: dto?.CanVehicle ?? dto?.canVehicle ?? null,
      canAditionalVehicle: dto?.CanAditionalVehicle ?? dto?.CanAdditionalVehicle ?? dto?.canAditionalVehicle ?? dto?.canAdditionalVehicle ?? null,
      canClient: dto?.CanClient ?? dto?.canClient ?? null,
      canStock: dto?.CanStock ?? dto?.canStock ?? null,
      canServices: dto?.CanServices ?? dto?.canServices ?? null,
      active: dto?.Active ?? dto?.IsActive ?? dto?.active ?? dto?.isActive ?? null,
      canClose: dto?.CanClose ?? dto?.CanClosed ?? dto?.canClose ?? dto?.canClosed ?? null,
      closingStatusId: Number.isFinite(closingStatusId) && closingStatusId > 0 ? closingStatusId : null,
      arrayTransactionServiceOrderId: this.normalizeArrayTransactionServiceOrderId(
        dto?.ArrayTransactionServiceOrderId ?? dto?.arrayTransactionServiceOrderId
      ),
      group: dto?.Group
        ? {
            id: dto.Group.Id ?? dto.Group.id ?? 0,
            name: dto.Group.Name ?? dto.Group.name ?? '',
            description: dto.Group.Description ?? dto.Group.description ?? null
          }
        : null,
      department: dto?.Department
        ? {
            id: dto.Department.Id ?? dto.Department.id ?? 0,
            name: dto.Department.Name ?? dto.Department.name ?? '',
            description: dto.Department.Description ?? dto.Department.description ?? null,
            isActive: dto.Department.IsActive ?? dto.Department.isActive ?? null
          }
        : null
    };
  }

  private mapToApiPayload(payload: ConfigOrderCreate | ConfigOrderUpdate): any {
    const { isThirdParty, ...rest } = payload as any;

    if (isThirdParty === undefined) return rest;

    return {
      ...rest,
      CanThirdParty: isThirdParty
    };
  }

  private normalizeBoolean(value: any): boolean | null {
    if (value == null) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true' || v === '1' || v === 'sim' || v === 'yes') return true;
      if (v === 'false' || v === '0' || v === 'nao' || v === 'não' || v === 'no') return false;
    }
    return null;
  }

  private normalizeArrayTransactionServiceOrderId(value: any): number[] {
    if (value == null) return [];

    if (Array.isArray(value)) {
      return value
        .map(v => Number(v))
        .filter(v => Number.isFinite(v));
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      // Backend pode enviar "[]" como string JSON
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map(v => Number(v))
            .filter(v => Number.isFinite(v));
        }
      } catch {
        // ignore
      }

      // Fallback: "1,2,3"
      return trimmed
        .replace(/^\[|\]$/g, '')
        .split(',')
        .map(s => Number(String(s).trim()))
        .filter(v => Number.isFinite(v));
    }

    // Unknown shape
    return [];
  }
}
